import { FaqEntry } from "@jugl-web/ui-components";
import { DialogWithHero } from "@jugl-web/ui-components/web/DialogWithHero";
import { Button } from "@jugl-web/ui-kit";
import { isoToLocalDate, useTranslations } from "@jugl-web/utils";
import React, { useMemo, useState } from "react";
import format from "date-fns/format";
import { useNavigation } from "@web-src/modules/navigation/hooks/useNavigation";
import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import { ReactComponent as CoinIcon } from "./assets/coin.svg";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";

export const MessageCreditsDialog: React.FC<{
  isOpen: boolean;
  usage: number;
  allowed: number;
  renewsDate?: string;
  onRequestClose: () => void;
}> = ({ isOpen, usage, allowed, renewsDate, onRequestClose }) => {
  const { t } = useTranslations();
  const { navigateToPage } = useNavigation();
  const { requestExtraCreditsDialog, maxRollOverCredits } = useSubscriptionV2();
  const { locale } = Intl.DateTimeFormat().resolvedOptions();
  const [openId, setOpenId] = useState<string | null>(null);

  const questions = useMemo(
    () => [
      {
        id: "what-are-message-credits",
        title: t({
          id: "cx-sidebar.what-message-credits-used-for",
          defaultMessage: "What are message credits used for?",
        }),
        content: t({
          id: "cx-sidebar.what-counts-as-credit-usage",
          defaultMessage:
            "Credits are only deducted for automated replies sent by your AI Agent. Any messages sent during Playground testing or by a live human agent are not counted against the usage.",
        }),
      },
      {
        id: "can-get-more-credits",
        title: t({
          id: "cx-sidebar.how-can-get-more-credits",
          defaultMessage: "How can I get more credits?",
        }),
        content: t({
          id: "cx-sidebar.how-to-get-more-credits",
          defaultMessage:
            "You can easily get more credits by purchasing an add-on package or by upgrading to a higher-tier plan with a larger credit allowance.",
        }),
      },
      {
        id: "can-modify-subscription",
        title: t({
          id: "cx-sidebar.can-modify-jugl-subscription",
          defaultMessage: "Can I modify my Jugl subscription plan?",
        }),
        content: t({
          id: "cx-sidebar.modify-subscription-plan-description",
          defaultMessage:
            "Yes, you can upgrade, downgrade, or cancel your subscription at any time. Proration will automatically be applied based on your usage and the exact date you modify or cancel your plan.",
        }),
      },
    ],
    [t]
  );

  return (
    <DialogWithHero
      isOpen={isOpen}
      onClose={onRequestClose}
      className="w-[780px]"
      upperTitle={
        <span className="font-secondary text-[22px] leading-[33px] font-light text-grey-200">
          {t({
            id: "cx-sidebar.message-credits",
            defaultMessage: "Message Credits",
          })}
        </span>
      }
      title={
        <>
          <CoinIcon />
          <span className="font-secondary text-[40px] leading-[60px] font-semibold text-white">
            {t(
              {
                id: "cx-sidebar.message-credits-used",
                defaultMessage: "{used} of {total} Used",
              },
              {
                used: new Intl.NumberFormat(locale).format(usage),
                total: new Intl.NumberFormat(locale).format(allowed),
              }
            )}
          </span>
        </>
      }
      description={[
        renewsDate
          ? t(
              {
                id: "cx-sidebar.renews-date",
                defaultMessage: "Renews on {date}",
              },
              { date: format(isoToLocalDate(renewsDate), "MMMM d, yyyy") }
            )
          : undefined,
        `Unused credits roll over (Up to ${maxRollOverCredits.toLocaleString()} max)`,
      ]
        .filter(Boolean)
        .join(" • ")}
      headerChildren={
        <Button
          className="mt-[34px] h-12 w-[300px]"
          iconEnd={<ArrowIcon />}
          onClick={() => {
            onRequestClose();
            requestExtraCreditsDialog();
            navigateToPage("cxWorkspacePlans");
          }}
        >
          {t({
            id: "cx-sidebar.get-extra-credits",
            defaultMessage: "Get Extra Credits",
          })}
        </Button>
      }
    >
      <div className="flex flex-col items-center justify-center gap-5 px-[26px] py-7">
        <div className="font-secondary text-xl leading-[30px] font-semibold text-black">
          {t({
            id: "cpanel-page.got-questions",
            defaultMessage: "Got questions? We have answers! 🙌",
          })}
        </div>
        <div className="flex w-full flex-col gap-2">
          {questions.map((question) => (
            <FaqEntry
              key={question.id}
              question={question.title}
              answer={question.content}
              isOpen={openId === question.id}
              onOpenSwitch={() =>
                setOpenId((prev) => (prev === question.id ? null : question.id))
              }
            />
          ))}
        </div>
      </div>
    </DialogWithHero>
  );
};
